import { type GroupAnalysis, type MapData, type MaturityInfo, type RiskFlag, type RiskLevel, tokenMaturity } from '@pleco/shared';
import { fmtPct } from './fmt.js';
import { maxLevel, tradingRisk } from './tradingRisk.js';

/**
 * One plain-language verdict for a token, combining what the holder map
 * shows (concentration, launch bundles, fresh wallets) with what trading
 * shows (linked groups, groups that are selling, farmed traders).
 *
 * Engine "cluster" flags are left out on purpose: the UI shows linked
 * wallets as groups only, and two different "linked" numbers side by side
 * confuse more than they help.
 */
export interface Verdict {
  /** undefined while there is not enough data to judge. */
  level?: RiskLevel;
  headline: string;
  reasons: { level: RiskLevel; title: string; text: string; groupId?: string }[];
  groupsHeld: number;
  top10: number;
  holders: number;
  checking: boolean;
  maturity: MaturityInfo;
}

export const LEVEL_LABEL: Record<RiskLevel, string> = {
  low: 'Low risk',
  medium: 'Medium risk',
  high: 'High risk',
  critical: 'Very high risk',
};

const MAP_FLAGS_SHOWN = new Set(['bundle_launch', 'snipers', 'fresh_wallets', 'top10', 'deployer_holding', 'thin_liquidity']);

/** Beginner-friendly headline for each kind of red flag. */
const PLAIN_TITLE: Record<string, string> = {
  'Bundled launch': 'The launch was bundled',
  'Linked wallets hold supply': 'Linked wallets hold a lot of supply',
  'Big linked holders': 'Big holders are linked',
  'Group is selling': 'A linked group is selling',
  'Farmed traders': 'Many traders look like fake wallets',
  'Dev sold': 'The creator has sold',
  bundle_launch: 'The launch was bundled',
  snipers: 'Early snipers still hold supply',
  fresh_wallets: 'Many holders are brand-new wallets',
  top10: 'Supply is concentrated',
  deployer_holding: 'The creator still holds a large share',
  thin_liquidity: 'Liquidity is thin',
};

function plainMapFlag(f: RiskFlag, d: MapData): string {
  const s = d.metrics.supply;
  switch (f.id) {
    case 'bundle_launch':
      return `Wallets that bought in the same block as the launch still hold ${fmtPct(s.bundles, 1)} of the supply. That is usually the team or its bots.`;
    case 'snipers':
      return `Wallets that bought within minutes of launch, typically sniper bots, still hold ${fmtPct(s.snipers, 1)} of the supply and tend to sell into the first rally.`;
    case 'fresh_wallets':
      return `${fmtPct(s.freshWallets, 1)} of the supply sits in wallets created just before they bought, a common sign of manufactured holders.`;
    case 'top10':
      return `The ten largest wallets hold ${fmtPct(s.top10Adjusted, 1)} of the supply, so a handful of sellers could move the price sharply.`;
    case 'deployer_holding':
      return f.detail.replace('The deploying wallet still holds', "The creator's wallet still holds").replace(/ of supply\.$/, ' of the supply.');
    case 'thin_liquidity':
      return `Only ${fmtPct(s.dex, 1)} of the supply sits in the trading pool, so relatively small orders can move the price a lot.`;
    default:
      return f.detail;
  }
}

/** Launch-era map flags that say nothing about a months-old, widely traded token. */
const LAUNCH_ONLY = new Set(['bundle_launch', 'snipers', 'thin_liquidity', 'deployer_holding']);
const ORDER: RiskLevel[] = ['low', 'medium', 'high', 'critical'];
const cap = (l: RiskLevel, max: RiskLevel): RiskLevel => (ORDER.indexOf(l) > ORDER.indexOf(max) ? max : l);

export function buildVerdict(data: MapData, analysis?: GroupAnalysis): Verdict {
  const m = data.metrics;
  const maturity = tokenMaturity(data.token);
  const est = maturity.level === 'established';
  const groupsHeld = analysis?.groups.reduce((s, g) => s + g.stats.holdingPercent, 0) ?? 0;
  const checking = !analysis || analysis.progress.tracing;
  const trading = tradingRisk(analysis, maturity.level);

  const reasons: Verdict['reasons'] = trading.flags.map((f) => ({
    level: f.level,
    title: PLAIN_TITLE[f.title] ?? f.title,
    text: f.detail.replace(/ — /g, ', '),
    groupId: f.groupId,
  }));
  const mapFlags = m.risk.notAssessable
    ? []
    : m.risk.flags.filter((f) => MAP_FLAGS_SHOWN.has(f.id) && f.level !== 'low' && !(maturity.level !== 'new' && LAUNCH_ONLY.has(f.id)));
  for (const f of mapFlags) {
    // Concentration is normal for a large project token (treasury, team, exchanges).
    const lvl = est ? cap(f.level, 'medium') : maturity.level === 'growing' ? cap(f.level, 'high') : f.level;
    reasons.push({ level: lvl, title: PLAIN_TITLE[f.id] ?? f.title, text: plainMapFlag(f, data) });
  }
  reasons.sort((a, b) => ORDER.indexOf(b.level) - ORDER.indexOf(a.level));

  const levels = reasons.map((r) => r.level);
  let level: RiskLevel | undefined =
    levels.length > 0 ? maxLevel(...levels) : m.risk.notAssessable ? undefined : checking ? undefined : 'low';
  // An established token is never "very high risk" from its holder layout
  // alone; only a linked group that is selling right now lifts it to high.
  const selling = trading.flags.some((f) => f.title === 'Group is selling');
  if (level && est) level = cap(level, selling ? 'high' : 'medium');
  else if (level && maturity.level === 'growing' && !selling) level = cap(level, 'high');

  const launchBundle = trading.flags.find((f) => f.title === 'Bundled launch');
  const headline = m.risk.notAssessable
    ? 'Not enough holders to judge yet'
    : est
      ? selling
        ? 'Established token, but a linked group is selling'
        : groupsHeld >= 10 || m.supply.top10Adjusted >= 50
          ? 'Established token with concentrated holders'
          : 'Established token, no active warning signs'
    : groupsHeld >= 10
      ? `Linked wallets hold ${fmtPct(groupsHeld, 1)} of the supply`
      : launchBundle
        ? 'The launch was bundled'
        : m.supply.top10Adjusted >= 50
          ? `The top 10 wallets hold ${fmtPct(m.supply.top10Adjusted, 1)}`
          : level === 'low'
            ? 'No major warning signs found'
            : (reasons[0]?.title ?? 'Looking for linked wallets…');

  return {
    level,
    headline,
    reasons: reasons.slice(0, 4),
    groupsHeld,
    top10: m.supply.top10Adjusted,
    holders: m.holders,
    checking,
    maturity,
  };
}
